import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isLegacySegmentOut } from '@/services/db/segment-out-integration/entity';
import { getSegmentOutIntegrationQueryOptions } from '@/services/db/segment-out-integration/queries';
import SegmentOutPage from './index.page';

const LOADING_LABEL = 'Loading integration…';

/** The two spellings of this connector's URL. */
function useHandoffHrefs() {
  const router = useRouter();

  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/integrations/segment_out', params: { workspaceId } }).href,
    buildLegacy: (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/integrations/segment_out',
        params: { workspaceId },
      }).href,
  });
}

/**
 * The old `/journeys/integrations/segment_out` URL.
 *
 * This URL answers for the workspace's record, not just for the flag. A record
 * without `use_javascript` predates the migrated form, so the migrated page hands
 * it back here (FPS-3689) — and if this URL forwarded it straight back the two
 * would bounce forever. Both hops replace their history entry, so it would spin in
 * place rather than filling history, which is why the flag alone is not enough.
 *
 * The read is skipped entirely while the flag is off: Ember owns the URL outright
 * then, and an account without the flag must not make the request.
 */
export function JourneysSegmentOutHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { migrated, workspaceId } = useHandoffHrefs();

  // The id comes off the reactive location, not `requireWorkspaceId()`: this view
  // renders under `/workspaces/last` before the resolver rewrites it, and reading
  // the id from the URL there throws.
  const record = useQuery({
    ...getSegmentOutIntegrationQueryOptions(Number(workspaceId)),
    enabled: isMigrated && workspaceId != null,
  });

  if (isMigrated && (workspaceId == null || record.isPending)) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  // A failed read leaves `data` undefined, which forwards to the migrated page —
  // where the reader gets the error, rather than a silent fall-through to Ember.
  const isLegacy = record.data != null && isLegacySegmentOut(record.data);

  return <JourneysConnectorHandoff isMigrated={isMigrated && !isLegacy} migratedHref={migrated} />;
}

/**
 * The migrated `/integrations/segment_out` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route applied.
 * This page's own route added no further gate — the Segment permissions gate the
 * controls, not the page — so nothing nests inside it here.
 *
 * The page is given the `journeys` URL as well: a legacy record has to be handed
 * back to the embedded app, and only the page knows once the record has loaded.
 */
export function SegmentOutRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { legacy } = useHandoffHrefs();

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) =>
        legacy == null ? (
          <PageLoader label={LOADING_LABEL} />
        ) : (
          <SegmentOutPage workspaceId={workspaceId} legacyHref={legacy} />
        )
      }
    </ConnectorRouteView>
  );
}
